import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiFetch } from '../lib/api';

interface FeatureToggles {
  pricing: boolean;
  billing: boolean;
  aiFeatures: boolean;
  dataGenerator: boolean;
  helpCenter: boolean;
  [key: string]: boolean;
}

const DEFAULT_TOGGLES: FeatureToggles = {
  pricing: true,
  billing: true,
  aiFeatures: true,
  dataGenerator: true,
  helpCenter: true,
};

interface FeatureToggleContextType {
  toggles: FeatureToggles;
  isEnabled: (feature: string) => boolean;
  setToggle: (feature: string, enabled: boolean) => void;
  refresh: () => Promise<void>;
}

const FeatureToggleContext = createContext<FeatureToggleContextType>({
  toggles: DEFAULT_TOGGLES,
  isEnabled: () => true,
  setToggle: () => {},
  refresh: async () => {},
});

export function FeatureToggleProvider({ children }: { children: ReactNode }) {
  const [toggles, setToggles] = useState<FeatureToggles>(() => {
    try {
      const stored = localStorage.getItem('feature-toggles');
      return stored ? { ...DEFAULT_TOGGLES, ...JSON.parse(stored) } : DEFAULT_TOGGLES;
    } catch { return DEFAULT_TOGGLES; }
  });

  const refresh = async () => {
    try {
      const res = await apiFetch('/api/system/feature-toggles');
      if (res.ok) {
        const data = await res.json();
        const merged = { ...DEFAULT_TOGGLES, ...data };
        setToggles(merged);
        localStorage.setItem('feature-toggles', JSON.stringify(merged));
      }
    } catch {
      // Use localStorage fallback
    }
  };

  useEffect(() => { refresh(); }, []);

  // Re-fetch toggles when user returns to the tab (catches admin panel changes)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Also poll every 60s for real-time sync
    const interval = setInterval(refresh, 60000);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const isEnabled = (feature: string) => toggles[feature] !== false;

  const setToggle = async (feature: string, enabled: boolean) => {
    const updated = { ...toggles, [feature]: enabled };
    setToggles(updated);
    localStorage.setItem('feature-toggles', JSON.stringify(updated));
    // Persist to server
    try {
      await apiFetch('/api/system/feature-toggles', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
    } catch {}
  };

  return (
    <FeatureToggleContext.Provider value={{ toggles, isEnabled, setToggle, refresh }}>
      {children}
    </FeatureToggleContext.Provider>
  );
}

export const useFeatureToggle = () => useContext(FeatureToggleContext);
